import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Undo2 } from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { apiClient } from '@/lib/api-client';
import { apiErrorMessage } from '@/lib/api-error';
import { formatUZS } from '@/lib/format';
import { FISCAL_STATUSES, fiscalColors, tiyinToSom, type FiscalReceipt, type FiscalStatus } from '@/lib/fiscal';
import { adminFiscalReceiptKey, useAdminFiscalReceipt, useAdminFiscalReceipts } from '@/hooks/use-fiscal-receipts';
import { cn } from '@/lib/utils';
import { intlLocale } from './adminLocale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export function FiscalBadge({ status }: { status: FiscalStatus }) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', fiscalColors[status])}>
      {t(`fiscal.status.${status}`)}
    </span>
  );
}

function Row({ label, value, mono }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border last:border-0 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('text-foreground text-right break-all', mono && 'font-mono text-xs')}>{value === null || value === undefined || value === '' ? '—' : value}</span>
    </div>
  );
}

/** FR-105: receipt card with the manual refund (only for an accepted sale receipt). */
export function FiscalReceiptDetail({ id, canRefund, onChanged }: { id: number; canRefund: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: receipt, isLoading } = useAdminFiscalReceipt(id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (isLoading || !receipt) return <div className="p-6"><Loader2 className="animate-spin" size={18} /></div>;

  const refund = async () => {
    setBusy(true);
    try {
      const { error } = await apiClient.POST('/admin/fiscal-receipts/{fiscalReceipt}/refund', { params: { path: { fiscalReceipt: receipt.id } } });
      if (error) throw error;
      toast.success(t('fiscal.admin.refund_done'));
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: adminFiscalReceiptKey(id) });
      onChanged();
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          {t(`fiscal.type.${receipt.receipt_type}`)} #{receipt.id} <FiscalBadge status={receipt.status} />
        </SheetTitle>
      </SheetHeader>
      <div>
        <Row label={t('fiscal.admin.merchant')} value={receipt.merchant?.name} />
        <Row label={t('fiscal.admin.case')} value={receipt.murabaha_case_id} />
        <Row label={t('fiscal.admin.order')} value={receipt.order_number} mono />
        <Row label={t('fiscal.amount')} value={formatUZS(tiyinToSom(receipt.amount_tiyin))} />
        <Row label={t('fiscal.vat', { percent: receipt.vat_percent })} value={formatUZS(tiyinToSom(receipt.vat_tiyin))} />
        <Row label={t('fiscal.admin.terminal')} value={receipt.terminal_id} mono />
        <Row label={t('fiscal.fiscal_sign')} value={receipt.fiscal_sign} mono />
        <Row label={t('fiscal.receipt_id')} value={receipt.receipt_id} mono />
        <Row label={t('fiscal.admin.attempts')} value={receipt.attempts} />
        <Row label={t('fiscal.admin.accepted_at')} value={receipt.accepted_at ? new Date(receipt.accepted_at).toLocaleString(intlLocale()) : null} />
        {receipt.error_message && <Row label={t('fiscal.admin.error')} value={receipt.error_message} />}
      </div>
      {receipt.items.length > 0 && (
        <div className="rounded-lg border border-border divide-y divide-border text-sm">
          {receipt.items.map((i, idx) => (
            <div key={idx} className="flex justify-between gap-3 px-3 py-2">
              <span>{i.name} × {i.quantity}</span>
              <span className="whitespace-nowrap">{formatUZS(tiyinToSom(i.total))}</span>
            </div>
          ))}
        </div>
      )}
      {canRefund && receipt.receipt_type === 'sale' && receipt.status === 'accepted' && (
        <Button variant="outline" className="text-destructive" onClick={() => setConfirmOpen(true)}>
          <Undo2 size={14} className="mr-1.5" /> {t('fiscal.admin.refund')}
        </Button>
      )}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('fiscal.admin.refund_title')}</DialogTitle>
            <DialogDescription>{t('fiscal.admin.refund_hint')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" disabled={busy} onClick={refund}>
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} {t('fiscal.admin.refund')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminFiscalReceipts() {
  const { t } = useTranslation();
  const { hasRole } = useAdminAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<FiscalStatus | 'all'>('all');
  const [merchantId, setMerchantId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  const { data: rows = [], isLoading } = useAdminFiscalReceipts({
    status,
    merchant_id: merchantId === 'all' ? undefined : Number(merchantId),
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });
  const { data: merchantsData } = useQuery({ queryKey: ['/admin/merchants'] });
  const merchants = (merchantsData as { data?: { id: number; name: string }[] } | undefined)?.data ?? [];

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-foreground">{t('fiscal.admin.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('fiscal.admin.subtitle')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={(v) => setStatus(v as FiscalStatus | 'all')}>
          <SelectTrigger className="w-[170px]" aria-label={t('fiscal.admin.status')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('fiscal.admin.all_statuses')}</SelectItem>
            {FISCAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`fiscal.status.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={merchantId} onValueChange={setMerchantId}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder={t('fiscal.admin.merchant')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('fiscal.admin.all_merchants')}</SelectItem>
            {merchants.map((m) => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" aria-label={t('fiscal.admin.date_from')} />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" aria-label={t('fiscal.admin.date_to')} />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr className="border-b border-border text-left">
              <th className="px-4 py-3 font-medium">#</th>
              <th className="px-4 py-3 font-medium">{t('fiscal.admin.merchant')}</th>
              <th className="px-4 py-3 font-medium">{t('fiscal.admin.type')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('fiscal.amount')}</th>
              <th className="px-4 py-3 font-medium">{t('fiscal.fiscal_sign')}</th>
              <th className="px-4 py-3 font-medium">{t('fiscal.admin.status')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground"><Loader2 className="inline animate-spin" size={16} /></td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">{t('fiscal.admin.empty')}</td></tr>}
            {rows.map((r: FiscalReceipt) => (
              <tr key={r.id} onClick={() => setOpenId(r.id)} className="border-b border-border last:border-0 hover:bg-muted/40 cursor-pointer">
                <td className="px-4 py-3 text-muted-foreground">{r.id}</td>
                <td className="px-4 py-3">{r.merchant?.name ?? '—'}</td>
                <td className="px-4 py-3">{t(`fiscal.type.${r.receipt_type}`)}</td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatUZS(tiyinToSom(r.amount_tiyin))}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.fiscal_sign ?? '—'}</td>
                <td className="px-4 py-3"><FiscalBadge status={r.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={openId !== null} onOpenChange={(v) => { if (!v) setOpenId(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          {openId !== null && (
            <FiscalReceiptDetail id={openId} canRefund={hasRole('platform_owner', 'finance_admin')} onChanged={() => qc.invalidateQueries({ queryKey: ['admin-fiscal-receipts'] })} />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
